// src/components/DevisFoodinnovModal.tsx

import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import { FirestoreDevis } from "../types/firestore";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import "../styles/Modal.css";
import "../styles/DevisModal.css";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

interface Opportunity {
  id?: string;
  contact: string;
  type: string;
  amount: number;
  date: string;
  createdAt: string;
  isContratCadre?: boolean;
}

interface Props {
  show: boolean;
  handleClose: () => void;
  initialData?: FirestoreDevis;
  preselectedOpportunity?: any | null; // Opportunité pré-sélectionnée depuis la navigation
}

const DevisFoodinnovModal: React.FC<Props> = ({
  show,
  handleClose,
  initialData,
  preselectedOpportunity,
}) => {
  // ------------ états ------------
  const [numero, setNumero] = useState<string>("");                // nouveau champ
  const [companies, setCompanies] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState<string>("");
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [selectedOppIndex, setSelectedOppIndex] = useState<number | "">("");

  const [contact, setContact] = useState<string>("");
  const [opportunityType, setOpportunityType] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const [tva, setTva] = useState<number>(20);
  const [tvaIsCustom, setTvaIsCustom] = useState(false);
  const [date, setDate] = useState<string>("");
  const [isSigned, setIsSigned] = useState<boolean>(false);
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [signedFileUrl, setSignedFileUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false); // Loader pour l'upload
  const [devisFile, setDevisFile] = useState<File | null>(null);
  const [devisFileUrl, setDevisFileUrl] = useState<string | null>(null);

  // 0️⃣ Remise à zéro pour l'ajout
  useEffect(() => {
    if (show && !initialData) {
      setNumero("");
      setCompanyId("");
      setOpportunities([]);
      setSelectedOppIndex("");
      setContact("");
      setOpportunityType("");
      setAmount(0);
      setDate(new Date().toISOString().slice(0, 10));
      setIsSigned(false);
      setSignedFile(null);
      setSignedFileUrl(null);
      setDevisFile(null);
      setDevisFileUrl(null);
      setTva(20);
      setTvaIsCustom(false);
    }
  }, [show, initialData]);

  // 1️⃣ Charger les sociétés
  useEffect(() => {
    getDoc(doc(db, "prospects", "foodinnovCompanies")).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const keys = Object.keys(data).filter((k) => {
        const arr = (data as any)[k]?.opportunities;
        return Array.isArray(arr) && arr.length > 0;
      }).sort((a, b) => a.localeCompare(b)); // Tri alphabétique
      setCompanies(keys);
    });
  }, []);

  // 2️⃣ Pré‐remplir en édition
  useEffect(() => {
    if (!initialData) return;
    setNumero(initialData.id || "");
    setCompanyId(initialData.companyId);
    setContact(initialData.contact);
    setOpportunityType(initialData.opportunityType);
    setAmount(initialData.amount);
    setDate(
      initialData.date instanceof Timestamp
        ? initialData.date.toDate().toISOString().slice(0, 10)
        : initialData.date
    );
    setIsSigned(initialData.isSigned || false);
    setSignedFileUrl(initialData.signedFileUrl || null);
    setDevisFileUrl(initialData.devisFileUrl || null);
    setDevisFile(null);
    const tvaValue = (initialData as any).tva !== undefined ? (initialData as any).tva : 20;
    setTva(tvaValue);
    setTvaIsCustom(tvaValue !== 20);
    // Note: selectedOppIndex sera défini dans l'useEffect suivant quand les opportunités seront chargées
  }, [initialData]);

  // Pré-sélectionner l'opportunité si elle est passée en prop
  useEffect(() => {
    if (show && preselectedOpportunity && !initialData) {
      setCompanyId(preselectedOpportunity.companyId);
      // Les opportunités seront chargées dans l'useEffect suivant
    }
  }, [show, preselectedOpportunity, initialData]);

  // 3️⃣ Recharger opportunités quand companyId change
  useEffect(() => {
    if (!companyId) {
      setOpportunities([]);
      setSelectedOppIndex("");
      return;
    }
    getDoc(doc(db, "prospects", "foodinnovCompanies")).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const arr = (data as any)[companyId]?.opportunities || [];
      const ops: Opportunity[] = arr.map((o: any) => ({
        id: o.id,
        contact: o.contact,
        type: o.type,
        amount: o.amount,
        date:
          o.date instanceof Timestamp
            ? o.date.toDate().toISOString().slice(0, 10)
            : o.date,
        createdAt:
          o.createdAt instanceof Timestamp
            ? o.createdAt.toDate().toISOString()
            : o.createdAt || "",
        isContratCadre: o.isContratCadre || false,
      }));
      setOpportunities(ops);

      // en édition, sélectionner par ID de l'opportunité
      if (initialData && initialData.companyId === companyId) {
        const idx = ops.findIndex((o) => o.id === initialData.opportunityId);
        if (idx >= 0) {
          setSelectedOppIndex(idx);
        } else {
          // Fallback: chercher par type si pas d'ID
          const fallbackIdx = ops.findIndex((o) => o.type === initialData.opportunityType);
          if (fallbackIdx >= 0) setSelectedOppIndex(fallbackIdx);
        }
      } else if (preselectedOpportunity && preselectedOpportunity.companyId === companyId) {
        // Pré-sélectionner l'opportunité passée en prop
        const matchIndex = ops.findIndex((o) => {
          // Comparer par id, type, qte, pu, et date
          const matchId = o.id === preselectedOpportunity.id;
          const matchType = o.type === preselectedOpportunity.type;
          const matchQte = (o as any).qte === preselectedOpportunity.qte;
          const matchPu = (o as any).pu === preselectedOpportunity.pu;
          
          // Normaliser les dates pour la comparaison
          const normalizeDate = (d: any) => {
            if (!d) return null;
            if (d instanceof Timestamp) return d.toDate().toISOString().slice(0, 10);
            if (typeof d === 'string') return d.slice(0, 10);
            if (d instanceof Date) return d.toISOString().slice(0, 10);
            return null;
          };
          const matchDate = normalizeDate(o.date) === normalizeDate(preselectedOpportunity.date);
          
          return matchId || (matchType && matchQte && matchPu && matchDate);
        });
        
        if (matchIndex >= 0) {
          setSelectedOppIndex(matchIndex);
        } else {
          // Si l'opportunité n'est pas trouvée, pré-remplir directement les champs
          setContact(preselectedOpportunity.contact || "");
          setOpportunityType(preselectedOpportunity.type || "");
          setAmount(preselectedOpportunity.amount || 0);
          
          // Gérer la date
          let dateStr = "";
          if (preselectedOpportunity.date) {
            if (preselectedOpportunity.date instanceof Timestamp) {
              dateStr = preselectedOpportunity.date.toDate().toISOString().slice(0, 10);
            } else if (typeof preselectedOpportunity.date === 'string') {
              dateStr = preselectedOpportunity.date.slice(0, 10);
            } else if (preselectedOpportunity.date instanceof Date) {
              dateStr = preselectedOpportunity.date.toISOString().slice(0, 10);
            }
          }
          if (dateStr) setDate(dateStr);
        }
      }
    });
  }, [companyId, initialData, preselectedOpportunity]);

  // 4️⃣ Pré-remplir champs quand opportunité sélectionnée
  useEffect(() => {
    if (selectedOppIndex === "") return;
    const o = opportunities[selectedOppIndex as number];
    setContact(o.contact);
    setOpportunityType(o.type);
    // Ne pas écraser le montant du devis avec celui de l'opportunité lors de l'édition
    if (!initialData) {
      setAmount(o.amount);
    }
    setDate(o.date);
  }, [selectedOppIndex, opportunities, initialData]);

  // ------------ enregistrement ------------
  const handleSubmit = async () => {
    if (uploading) return; // Empêche l'envoi multiple
    if (!companyId) {
      toast.error("Veuillez choisir une société.");
      return;
    }
    if (!opportunityType) {
      toast.error("Veuillez renseigner le type d'opportunité.");
      return;
    }
    if (!amount || isNaN(amount)) {
      toast.error("Veuillez renseigner le montant.");
      return;
    }

    let uploadedFileUrl = null;
    if (isSigned && signedFile) {
      setUploading(true);
      try {
        const fileRef = storageRef(storage, `devisSignés/${companyId}/${signedFile.name}`);
        await uploadBytes(fileRef, signedFile);
        uploadedFileUrl = await getDownloadURL(fileRef);
        setSignedFileUrl(uploadedFileUrl);
        toast.success("Fichier signé uploadé avec succès !");
      } catch (err) {
        toast.error("Erreur lors de l'upload du fichier signé sur Firebase Storage.");
        setUploading(false);
        return;
      }
      setUploading(false);
    } else if (isSigned && signedFileUrl) {
      uploadedFileUrl = signedFileUrl;
    }

    let uploadedDevisFileUrl = null;
    if (devisFile) {
      setUploading(true);
      try {
        const fileRef = storageRef(storage, `devisSignés/${companyId}/${devisFile.name}`);
        await uploadBytes(fileRef, devisFile);
        uploadedDevisFileUrl = await getDownloadURL(fileRef);
        setDevisFileUrl(uploadedDevisFileUrl);
        toast.success("Fichier devis uploadé avec succès !");
      } catch (err) {
        toast.error("Erreur lors de l'upload du fichier devis sur Firebase Storage.");
        setUploading(false);
        return;
      }
      setUploading(false);
    } else if (devisFileUrl) {
      uploadedDevisFileUrl = devisFileUrl;
    }

    const ref = doc(db, "prospects", "foodinnovCompanies");
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data()! : {};

    const selectedOpportunity = selectedOppIndex !== "" ? opportunities[selectedOppIndex as number] : null;
    
    // Créer l'objet devis en filtrant les valeurs undefined
    const newDevis: any = {
      id: numero,
      companyId,
      contact,
      opportunityType: selectedOpportunity?.type || opportunityType,
      amount: amount,
      date,
      isSigned,
      updatedAt: Timestamp.now(),
      isContratCadre: selectedOpportunity?.isContratCadre || false,
      tva: tva,
    };

    // Ajouter les champs optionnels seulement s'ils ne sont pas undefined
    if (selectedOpportunity?.id) {
      newDevis.opportunityId = selectedOpportunity.id;
    }
    if (uploadedFileUrl) {
      newDevis.signedFileUrl = uploadedFileUrl;
    }
    if (uploadedDevisFileUrl) {
      newDevis.devisFileUrl = uploadedDevisFileUrl;
    }

    const oldList: FirestoreDevis[] = Array.isArray(
      (data as any)[companyId]?.devis
    )
      ? (data as any)[companyId].devis
      : [];

    console.log("handleSubmit - initialData:", initialData);
    console.log("handleSubmit - newDevis:", newDevis);
    console.log("handleSubmit - oldList before filter:", oldList);

    const updatedList = initialData
      ? [...oldList.filter((d) => d.id !== initialData.id), newDevis]
      : [...oldList, newDevis];

    await updateDoc(ref, {
      [`${companyId}.devis`]: updatedList,
    });
    toast.success(initialData ? "Devis mis à jour." : "Nouveau devis ajouté.");
    handleClose();
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>{initialData ? "Modifier un devis" : "Ajouter un Devis"}</h3>

        {/* Champ numéro de devis en premier */}
        <div className="form-group">
          <label>Numéro de devis (optionnel)</label>
          <input
            type="text"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="Ex : D25007"
          />
        </div>

        <div className="form-group">
          <label>Société</label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">-- Choisir une société --</option>
            {companies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Opportunité</label>
          <select
            value={selectedOppIndex}
            onChange={(e) =>
              setSelectedOppIndex(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          >
            <option value="">-- Choisir une opportunité --</option>
            {opportunities.map((o, i) => (
              <option key={i} value={i}>
                {`${o.type} — ${new Date(o.date).toLocaleDateString(
                  "fr-FR"
                )}`}
              </option>
            ))}
          </select>
        </div>

        {/* Groupe de champs Contact, Type d'opportunité, Montant en grille 3 colonnes */}
        <div className="form-row-3">
          <div className="form-group">
            <label>Contact</label>
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Type d'opportunité</label>
            <input
              type="text"
              value={opportunityType}
              onChange={(e) => setOpportunityType(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Montant HT</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={tvaIsCustom}
              onChange={(e) => {
                const checked = e.target.checked;
                setTvaIsCustom(checked);
                if (!checked) setTva(20);
              }}
              style={{ width: 'auto', cursor: 'pointer' }}
            />
            TVA différente de 20%
          </label>
          {tvaIsCustom && (
            <input
              type="number"
              value={tva}
              onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.1"
              placeholder="Ex: 0"
              style={{ marginTop: '8px' }}
            />
          )}
          {amount > 0 && (
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
              {tva === 0
                ? `TVA 0% — Montant TTC = HT : ${amount.toFixed(2)}€`
                : `Montant TTC : ${amount.toFixed(2)}€ × ${(1 + tva / 100).toFixed(2)} (TVA ${tva}%) = ${(amount * (1 + tva / 100)).toFixed(2)}€`
              }
            </small>
          )}
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>


        {/* Afficher la case à cocher uniquement en édition */}
        {initialData && (
          <div className="form-group signed-checkbox-form-group" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-start' }}>
            <input
              type="checkbox"
              checked={isSigned}
              onChange={(e) => setIsSigned(e.target.checked)}
              style={{ margin: 0 }}
            />
            <span>Devis signé</span>
            {isSigned && (
              <>
                <i
                  className="bi bi-paperclip"
                  style={{ color: '#007bff', fontSize: '1.3em', verticalAlign: 'middle', fontWeight: 'bold', cursor: 'pointer' }}
                  onClick={() => document.getElementById('signed-file-input')?.click()}
                  title="Joindre un fichier signé"
                />
                <input
                  id="signed-file-input"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      setSignedFile(e.target.files[0]);
                    }
                  }}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {signedFile && (
                  <span style={{ fontSize: '0.9em', color: '#333' }}>
                    {signedFile.name}
                  </span>
                )}
                {!signedFile && signedFileUrl && (
                  <a
                    href={signedFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9em', color: '#007bff' }}
                  >
                    Voir le fichier signé
                  </a>
                )}
              </>
            )}
          </div>
        )}

        {/* Case à cocher pour afficher si l'opportunité est un contrat cadre */}
        {initialData && (
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-start' }}>
            <input
              type="checkbox"
              checked={selectedOppIndex !== "" ? (opportunities[selectedOppIndex as number]?.isContratCadre || false) : false}
              disabled
              style={{ margin: 0, opacity: 0.6 }}
            />
            <span style={{ opacity: 0.8, fontStyle: 'italic' }}>Contrat Cadre (opportunité liée)</span>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" onClick={handleClose} className="cancel-button" style={{ backgroundColor: 'lightgrey', color: 'black' }}>
            Annuler
          </button>
          <button type="button" onClick={handleSubmit} className="submit-button" style={{ backgroundColor: 'lightblue', color: 'white' }} disabled={uploading}>
            {uploading ? (initialData ? "Enregistrement..." : "Création...") : (initialData ? "Enregistrer" : "Créer")}
          </button>
          {uploading && <span style={{ marginLeft: 10, color: '#2E86C1' }}>Envoi du fichier en cours...</span>}
        </div>
      </div>
    </div>
  );
};

export default DevisFoodinnovModal;
